import { useCallback, useEffect, useState } from 'react';
import { Button, Group, Textarea } from '@mantine/core';
import { IconClock, IconPencil } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getRobot, updateRobotConfigs } from '@mygpt/app/apis';
import { createPromptVersion, listPromptVersions } from '@mygpt/app/apis/services/prompt-versions';
import { useBot } from '@mygpt/app/bot';
import { showNotification } from '@mantine/notifications';
import { OptimizationModal } from '@/components/Settings/items/PromptOptimization/OptimizationModal';
import { VersionHistoryDrawer } from '@/components/Settings/items/PromptOptimization/VersionHistoryDrawer';

const RealtimePrompt = () => {
  const { t } = useTranslation();
  const { botId } = useBot();

  const [prompt, setPrompt] = useState('');
  const [savedPrompt, setSavedPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [versionCount, setVersionCount] = useState(0);
  const [optimizationOpened, setOptimizationOpened] = useState(false);
  const [historyOpened, setHistoryOpened] = useState(false);

  // 加载 voice_agent_prompt
  useEffect(() => {
    if (!botId) return;
    const load = async () => {
      try {
        setLoading(true);
        const { data } = await getRobot({ aiId: botId });
        const val = data.configs?.voice_agent_prompt || '';
        setPrompt(val);
        setSavedPrompt(val);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [botId]);

  const fetchVersionCount = useCallback(async () => {
    if (!botId) return;
    try {
      const res = await listPromptVersions(botId, 'realtime', 1, 1);
      setVersionCount(res.total);
    } catch {
      // ignore
    }
  }, [botId]);

  useEffect(() => {
    fetchVersionCount();
  }, [fetchVersionCount]);

  const hasChanges = prompt !== savedPrompt;

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await updateRobotConfigs({
        aiId: botId,
        configs: { voice_agent_prompt: prompt },
      });
      setSavedPrompt(prompt);
      try {
        await createPromptVersion(botId, { prompt, prompt_type: 'realtime', source: 'manual' });
      } catch {
        // ignore
      }
      showNotification({ message: t('save_success'), color: 'green.5' });
      fetchVersionCount();
    } catch (error: any) {
      showNotification({ message: error.message || 'Save failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => setPrompt(savedPrompt);

  const handleOptimizationApply = async (newPrompt: string, changeSummary?: string) => {
    await updateRobotConfigs({
      aiId: botId,
      configs: { voice_agent_prompt: newPrompt },
    });
    setPrompt(newPrompt);
    setSavedPrompt(newPrompt);
    try {
      await createPromptVersion(botId, {
        prompt: newPrompt,
        prompt_type: 'realtime',
        change_summary: changeSummary,
        source: 'ai_optimization',
      });
    } catch {
      // ignore
    }
    showNotification({ message: t('save_success'), color: 'green.5' });
    fetchVersionCount();
  };

  const handleVersionActivated = async () => {
    // 版本回滚后重新加载
    try {
      const { data } = await getRobot({ aiId: botId });
      const val = data.configs?.voice_agent_prompt || '';
      setPrompt(val);
      setSavedPrompt(val);
    } catch {
      // ignore
    }
    fetchVersionCount();
  };

  if (loading) return null;

  return (
    <div
      className="relative rounded-xl bg-white flex-1 p-6"
      style={{ boxShadow: '0px 10px 60px 0px rgba(226, 236, 249, 0.50' }}
    >
      <div className="flex justify-end mb-4">
        <Button leftSection={<IconPencil size={16} />} onClick={() => setOptimizationOpened(true)}>
          {t('settings.prompt.optimize')}
        </Button>
      </div>

      <div className="mb-2">
        <Group gap="sm" align="center">
          <span className="font-semibold">{t('settings.realtime_prompt.title')}</span>
          <Button
            variant="subtle"
            size="compact-xs"
            leftSection={<IconClock size={14} />}
            onClick={() => setHistoryOpened(true)}
          >
            {t('settings.prompt.version_history')} ({versionCount})
          </Button>
        </Group>
        <p className="text-sm text-gray-500 mt-1">{t('settings.realtime_prompt.description')}</p>
      </div>
      <Textarea
        placeholder={t('settings.realtime_prompt.title')}
        value={prompt}
        onChange={(e) => setPrompt(e.currentTarget.value)}
        minRows={10}
        maxRows={20}
        autosize
        resize="vertical"
      />

      <Group justify="flex-end" mt="xl">
        <Button variant="default" onClick={handleCancel} disabled={!hasChanges}>
          {t('settings.prompt.cancel')}
        </Button>
        <Button onClick={handleSave} loading={saving} disabled={!hasChanges}>
          {t('settings.prompt.save')}
        </Button>
      </Group>

      <OptimizationModal
        opened={optimizationOpened}
        onClose={() => setOptimizationOpened(false)}
        currentPrompt={prompt}
        promptType="realtime"
        onApply={handleOptimizationApply}
      />

      <VersionHistoryDrawer
        opened={historyOpened}
        onClose={() => setHistoryOpened(false)}
        aiId={botId}
        promptType="realtime"
        currentPrompt={prompt}
        onVersionActivated={handleVersionActivated}
      />
    </div>
  );
};

export default RealtimePrompt;
